import React, { useState, useEffect, useCallback, useRef } from 'react';
import { collection, query, where, getDocs, Timestamp, doc, updateDoc, deleteDoc, addDoc, getDoc, writeBatch } from 'firebase/firestore';
import { DndContext, DragOverlay, closestCenter, PointerSensor, useSensor, useSensors, useDroppable } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { X, Clock, User, Plus, RotateCcw, Calendar, Check, Loader, AlertCircle } from 'lucide-react';
import { db as firebaseDb } from '../firebase';
import ModernModal from './ModernModal';
import ClientAddAppointmentModal from './ClientAddAppointmentModal';
import EnhancedModal, { ModalHeader, ModalBody, ModalFooter, ModalButton } from './EnhancedModal';
import RecurrenceMoveModal from './RecurrenceMoveModal';
import RecurrenceEditModal from './RecurrenceEditModal';
import AppointmentViewModal from './AppointmentViewModal';
import { useTheme } from '../contexts/ThemeContext';
import {
  generateRecurringAppointments,
  shouldAddAppointment,
  getNextRecurrenceDate
} from '../utils/recurrenceUtils';
import '../ClientDayView.css';


// Fonction utilitaire pour comparer deux dates
const isSameDay = (date1, date2) => {
  if (!(date1 instanceof Date) || !(date2 instanceof Date)) {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    return d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate();
  }
  return date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate();
};

// Composant pour un rendez-vous draggable
const SortableAppointment = ({ appointment, position, getStatusClass, formatTime, handleAppointmentClick, handleAppointmentDoubleClick, canEditAppointments }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: appointment.id,
    disabled: !canEditAppointments
  });

  const style = {
    left: `${position.left}%`,
    width: `${position.width}%`,
    position: 'absolute',
    top: '50%',
    transform: CSS.Transform.toString(transform) || 'translateY(-50%)',
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? (window.isCopyModeContext ? 'copy' : 'grabbing') : (canEditAppointments ? (window.isCopyModeContext ? 'copy' : 'grab') : 'default'),
  };


  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        zIndex: isDragging ? 1000 : 1, // Augmenter le z-index pendant le drag
        border: canEditAppointments ? '2px solid #3b82f6' : 'none', // Bordure bleue pour les draggables
      }}
      {...attributes}
      {...listeners}
      className={`cdv-timeline-appointment ${getStatusClass(appointment.status)} ${isDragging ? 'dragging' : ''} ${canEditAppointments ? 'cdv-draggable' : ''}`}
      onClick={(e) => {
        e.stopPropagation();
        if (!isDragging) {
          handleAppointmentClick(appointment);
        }
      }}
      onDoubleClick={(e) => {
        e.stopPropagation();
        if (!isDragging) {
          handleAppointmentDoubleClick(appointment);
        }
      }}
    >
      <div className="cdv-appointment-content">
        <div className="cdv-appointment-time">
          {`${formatTime(appointment.startTime)} - ${formatTime(appointment.endTime)}`}
        </div>
        <div className="cdv-appointment-details">
          Rendez-vous
        </div>
        {appointment.notes && appointment.notes.content && (
          <div className="cdv-notes-indicator">
            📝
          </div>
        )}
      </div>
    </div>
  );
};

// Composant pour une zone droppable
const DroppableRow = ({ id, children, className, onClick }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: id,
  });

  return (
    <div
      ref={setNodeRef}
      className={`${className} ${isOver ? 'cdv-dragging-over' : ''}`}
      onClick={onClick}
    >
      {children}
    </div>
  );
};

const ClientDayView = React.memo(({ db, client, currentDate, formatDate, formatTime, hasPermission }) => {
  const safeDb = db || firebaseDb;
  const [appointments, setAppointments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [approvedLeaves, setApprovedLeaves] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [settings, setSettings] = useState({
    planningOpeningTime: '08:00',
    planningClosingTime: '18:00',
    showWeekends: true,
    firstDayOfWeek: 1,
  });
  const [showRecurrenceMoveModal, setShowRecurrenceMoveModal] = useState(false);
  const [showRecurrenceUpdateModal, setShowRecurrenceUpdateModal] = useState(false);
  const [pendingMoveData, setPendingMoveData] = useState(null);
  const [pendingUpdateData, setPendingUpdateData] = useState(null);
  const [clickTimeout, setClickTimeout] = useState(null);
  const [openInEditMode, setOpenInEditMode] = useState(false);
  const [isClosingModal, setIsClosingModal] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [isCopyMode, setIsCopyMode] = useState(false);
  const copyModeRef = useRef(false);



  // Gestion robuste des permissions avec hasPermission
  const canEditAppointments = hasPermission ? hasPermission({ resource: 'appointments', action: 'write' }) : false;

  // Configuration des sensors pour le drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1, // Distance d'activation réduite pour plus de réactivité
        tolerance: 5, // Tolérance pour éviter les faux positifs
      },
    })
  );

  const fetchEmployees = useCallback(async () => {
    if (!safeDb) return;
    try {
      const employeesRef = collection(safeDb, 'employees');
      // Récupérer tous les employés sans filtre isArchived côté Firestore
      const querySnapshot = await getDocs(employeesRef);
      const fetchedEmployees = querySnapshot.docs.map(doc => ({
        userId: doc.id,
        ...doc.data(),
        color: doc.data().color || '#808080',
        photoURL: doc.data().photoURL || null
      }));

      // Filtrer côté client pour inclure les employés où isArchived est false ou absent
      const filteredEmployees = fetchedEmployees.filter(employee => {
        // Exclure les comptes bénéficiaires (rôle 'beneficiaire')
        if (employee.role === 'beneficiaire') {
          return false;
        }

        // Inclure si isArchived est false ou absent/undefined
        const isNotArchived = employee.isArchived !== true;
        const isActive = employee.isActive !== false;

        return isNotArchived && isActive;
      });


      setEmployees(filteredEmployees);
    } catch (error) {
      // Erreur silencieuse lors de la récupération des employés
    }
  }, [db]);

  const fetchAppointments = useCallback(async () => {
    if (!db || !client.id) return;
    try {
      const startOfDay = new Date(currentDate);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(currentDate);
      endOfDay.setHours(23, 59, 59, 999);

      const appointmentsRef = collection(db, 'appointments');
      const q = query(
        appointmentsRef,
        where('clientId', '==', client.id),
        where('date', '>=', Timestamp.fromDate(startOfDay)),
        where('date', '<=', Timestamp.fromDate(endOfDay))
      );
      const querySnapshot = await getDocs(q);
      const fetchedAppointments = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date?.toDate() || new Date(),
      }));
      setAppointments(fetchedAppointments);
    } catch (error) {
      // Erreur silencieuse lors de la récupération des rendez-vous
    }
  }, [db, client.id, currentDate]);

  useEffect(() => {
    fetchEmployees();
    fetchAppointments();
  }, [fetchEmployees, fetchAppointments, refreshTrigger]);

  useEffect(() => {
    const fetchSettings = async () => {
      if (!db) return;

      // Récupérer depuis general settings
      const settingsDoc = await getDoc(doc(db, 'settings', 'general'));
      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        setSettings({
          planningOpeningTime: data.planningOpeningTime || '08:00',
          planningClosingTime: data.planningClosingTime || '18:00',
          showWeekends: data.showWeekends !== false,
          firstDayOfWeek: data.firstDayOfWeek || 1,
        });
      }
    };
    fetchSettings();
  }, [db]);

  const handleAppointmentClick = (appointment) => {
    // Annuler le timeout du clic simple si un double clic est détecté
    if (clickTimeout) {
      clearTimeout(clickTimeout);
      setClickTimeout(null);
      return;
    }

    // Empêcher l'ouverture si le modal est en cours de fermeture
    if (isClosingModal) {
      return;
    }

    // Définir un timeout pour le clic simple
    const timeout = setTimeout(() => {
      // Simple clic : affiche les détails en mode consultation
      setSelectedAppointment(appointment);
      setIsViewModalOpen(true);
      setClickTimeout(null);
    }, 300); // Délai de 300ms pour détecter le double clic

    setClickTimeout(timeout);
  };

  const handleAppointmentDoubleClick = (appointment) => {
    // Annuler le timeout du clic simple
    if (clickTimeout) {
      clearTimeout(clickTimeout);
      setClickTimeout(null);
    }

    // Empêcher l'ouverture si le modal est en cours de fermeture
    if (isClosingModal) {
      return;
    }

    // Double clic : passe directement en mode modification si permissions
    if (canEditAppointments && appointment.status !== 'completed') {
      setSelectedAppointment(appointment);
      setIsModalOpen(true);
      setOpenInEditMode(true); // Marquer pour ouvrir en mode édition
    }
  };

  // Fonction pour ouvrir le modal en mode édition
  const openModalInEditMode = (appointment) => {
    setSelectedAppointment(appointment);
    setIsModalOpen(true);
    setOpenInEditMode(true);
  };

  const handleTimelineClick = (employeeId) => {
    if (!canEditAppointments) return;
    setSelectedEmployeeId(employeeId);
    setSelectedAppointment(null);
    setIsModalOpen(true);
  };

  const handleAddAppointment = async (appointmentData) => {
    if (!canEditAppointments) return;
    try {
      let appointmentsToAdd = [];
      if (appointmentData.isRecurring) {
        appointmentsToAdd = generateRecurringAppointments(appointmentData, safeDb);
      } else {
        appointmentsToAdd = [{
          ...appointmentData,
          isRecurring: false,
          status: appointmentData.employeeId ? 'assigne' : 'libre'
        }];
      }

      const batch = writeBatch(safeDb);

      appointmentsToAdd.forEach(appointment => {
        const newAppointmentRef = doc(collection(safeDb, 'appointments'));

        // Trouver le nom de l'employé
        let employeeName = '';
        if (appointment.employeeId) {
          const employee = employees.find(emp => emp.userId === appointment.employeeId);
          if (employee) {
            employeeName = employee.name || `${employee.firstName} ${employee.lastName}`;
          }
        }

        batch.set(newAppointmentRef, {
          ...appointment,
          clientId: client.id,
          employeeName: employeeName,
          date: Timestamp.fromDate(appointment.date)
        });
      });

      await batch.commit();

      setIsModalOpen(false);
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      // Erreur silencieuse lors de l'ajout du/des rendez-vous
    }
  };

  const handleUpdateAppointment = async (updatedAppointment, scope = 'this') => {
    if (!canEditAppointments) return;
    try {
      if (scope === 'this') {
        // Cette occurrence uniquement
        const appointmentRef = doc(db, 'appointments', updatedAppointment.id);
        const status = updatedAppointment.employeeId ? 'assigne' : 'libre';

        // Trouver le nom de l'employé
        let employeeName = '';
        if (updatedAppointment.employeeId) {
          const employee = employees.find(emp => emp.userId === updatedAppointment.employeeId);
          if (employee) {
            employeeName = employee.name || `${employee.firstName} ${employee.lastName}`;
          }
        }

        await updateDoc(appointmentRef, {
          ...updatedAppointment,
          status: status,
          employeeName: employeeName
        });

      } else if (scope === 'future') {
        // Toutes les occurrences futures
        const allRecurringAppointments = await fetchAllRecurringAppointments(updatedAppointment);

        // Filtrer les occurrences futures (incluant celle-ci)
        const currentDate = new Date(updatedAppointment.date);
        currentDate.setHours(0, 0, 0, 0); // Normaliser l'heure pour la comparaison

        const appointmentsToUpdate = allRecurringAppointments.filter(app => {
          const appDate = new Date(app.date);
          appDate.setHours(0, 0, 0, 0); // Normaliser l'heure pour la comparaison
          return appDate >= currentDate;
        });

        // Mettre à jour tous les rendez-vous sélectionnés
        for (const app of appointmentsToUpdate) {
          const status = updatedAppointment.employeeId ? 'assigne' : 'libre';

          // Trouver le nom de l'employé
          let employeeName = '';
          if (updatedAppointment.employeeId) {
            const employee = employees.find(emp => emp.userId === updatedAppointment.employeeId);
            if (employee) {
              employeeName = employee.name || `${employee.firstName} ${employee.lastName}`;
            }
          }

          await updateDoc(doc(db, 'appointments', app.id), {
            ...updatedAppointment,
            id: app.id, // Garder l'ID original
            date: app.date, // Garder la date originale
            startTime: updatedAppointment.startTime,
            endTime: updatedAppointment.endTime,
            employeeId: updatedAppointment.employeeId,
            details: updatedAppointment.details,
            status: status,
            employeeName: employeeName
          });
        }
      }

      setIsModalOpen(false);
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      // Erreur silencieuse lors de la mise à jour du rendez-vous
    }
  };

  const handleDeleteAppointment = async (appointmentId, scope = 'this') => {
    if (!canEditAppointments) return;
    try {
      if (scope === 'this') {
        // Supprimer seulement ce rendez-vous
        await deleteDoc(doc(db, 'appointments', appointmentId));
      } else if (scope === 'future') {
        // Supprimer tous les rendez-vous futurs de la série
        const appointment = appointments.find(app => app.id === appointmentId);
        if (appointment && appointment.recurrenceSeriesId) {
          const appointmentsRef = collection(db, 'appointments');
          const q = query(
            appointmentsRef,
            where('clientId', '==', client.id),
            where('recurrenceSeriesId', '==', appointment.recurrenceSeriesId)
          );
          const querySnapshot = await getDocs(q);

          const batch = writeBatch(db);
          const currentDate = new Date(appointment.date);

          querySnapshot.docs.forEach(doc => {
            const appData = doc.data();
            const appDate = appData.date?.toDate() || new Date();
            if (appDate >= currentDate) {
              batch.delete(doc.ref);
            }
          });

          await batch.commit();
        }
      }
      setIsModalOpen(false);
      setIsViewModalOpen(false);
      setSelectedAppointment(null);
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      // Erreur silencieuse lors de la suppression du rendez-vous
    }
  };

  // Les fonctions de récurrence sont maintenant importées depuis recurrenceUtils.js
  // Cela évite la duplication de code et garantit la cohérence entre toutes les vues

  // Fonction pour générer les rendez-vous de remplacement temporaire
  const generateReplacementAppointments = (originalAppointment, newEmployeeId, employeeName, startDate, endDate, newSeriesId) => {
    const appointments = [];
    let currentDate = new Date(startDate);
    const status = newEmployeeId ? 'assigne' : 'libre';

    let occurrenceCount = 0;
    const maxOccurrences = 1000; // Limite de sécurité

    while (currentDate <= endDate && occurrenceCount < maxOccurrences) {
      if (originalAppointment.recurrenceBase === 'daily') {
        // Vérifier si on est dans la période
        if (currentDate >= startDate && currentDate <= endDate) {
          appointments.push({
            ...originalAppointment,
            date: new Date(currentDate),
            employeeId: newEmployeeId,
            employeeName: employeeName,
            status: status,
            isRecurring: true,
            recurrenceSeriesId: newSeriesId,
            id: undefined // L'ID sera généré par Firestore
          });
          occurrenceCount++;
        }
        currentDate.setDate(currentDate.getDate() + (originalAppointment.recurrenceFrequency || 1));

      } else if (originalAppointment.recurrenceBase === 'weekly') {
        // Vérifier si le jour de la semaine correspond
        if (originalAppointment.recurrenceDay.includes(currentDate.getDay()) &&
          currentDate >= startDate && currentDate <= endDate) {
          appointments.push({
            ...originalAppointment,
            date: new Date(currentDate),
            employeeId: newEmployeeId,
            employeeName: employeeName,
            status: status,
            isRecurring: true,
            recurrenceSeriesId: newSeriesId,
            id: undefined
          });
          occurrenceCount++;
        }
        currentDate.setDate(currentDate.getDate() + 1);

      } else if (originalAppointment.recurrenceBase === 'monthly') {
        // Vérifier si la date correspond aux paramètres mensuels
        if (shouldAddAppointment(currentDate, originalAppointment) &&
          currentDate >= startDate && currentDate <= endDate) {
          appointments.push({
            ...originalAppointment,
            date: new Date(currentDate),
            employeeId: newEmployeeId,
            employeeName: employeeName,
            status: status,
            isRecurring: true,
            recurrenceSeriesId: newSeriesId,
            id: undefined
          });
          occurrenceCount++;
        }
        currentDate = getNextRecurrenceDate(currentDate, originalAppointment);
      }
    }

    return appointments;
  };


  // Ajoutons la fonction pour récupérer les congés approuvés
  const fetchApprovedLeaves = useCallback(async () => {
    if (!db) return;
    try {
      const leavesRef = collection(db, 'leaves');
      const q = query(
        leavesRef,
        where('status', '==', 'approved')
      );
      const querySnapshot = await getDocs(q);
      const leaves = querySnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id,
        startDate: doc.data().startDate.toDate(),
        endDate: doc.data().endDate.toDate()
      }));
      setApprovedLeaves(leaves);
    } catch (error) {
      // Erreur silencieuse lors de la récupération des congés
    }
  }, [db]);

  // Ajoutons l'appel au useEffect existant
  useEffect(() => {
    fetchEmployees();
    fetchAppointments();
    fetchApprovedLeaves(); // Ajout de cette ligne
  }, [fetchEmployees, fetchAppointments, fetchApprovedLeaves, refreshTrigger]);

  // Gestion de la touche Ctrl pour le mode copie
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey || e.metaKey) {
        setIsCopyMode(true);
        copyModeRef.current = true;
      }
    };

    const handleKeyUp = (e) => {
      if (!e.ctrlKey && !e.metaKey) {
        setIsCopyMode(false);
        copyModeRef.current = false;
      }
    };

    const handleBlur = () => {
      setIsCopyMode(false);
      copyModeRef.current = false;
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleBlur);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  // Fonction pour vérifier si un employé est en congé à une date donnée
  const isEmployeeOnLeave = useCallback((employeeId) => {
    if (!approvedLeaves.length) return false;

    const today = new Date(currentDate);
    today.setHours(0, 0, 0, 0); // Normaliser l'heure à minuit

    return approvedLeaves.some(leave => {
      const startDate = new Date(leave.startDate);
      const endDate = new Date(leave.endDate);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);

      return leave.employeeId === employeeId &&
        today >= startDate &&
        today <= endDate;
    });
  }, [approvedLeaves, currentDate]);


  const handleDragStart = useCallback((event) => {
    setActiveId(event.active.id);
    const isDraggingWithCtrl = event.activatorEvent?.ctrlKey || event.activatorEvent?.metaKey || copyModeRef.current;
    setIsCopyMode(isDraggingWithCtrl);
    copyModeRef.current = isDraggingWithCtrl;
    window.isCopyModeContext = isDraggingWithCtrl; // Pour que SortableAppointment y ait accès
  }, []);

  const handleDragOver = useCallback((event) => {
    // Fonction vide pour le drag over
  }, []);

  // Fonction utilitaire pour convertir un temps en minutes
  const timeToMinutes = (time) => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Fonction utilitaire pour calculer une nouvelle heure de fin basée sur la durée originale
  const calculateNewEndTime = (startTime, endTime) => {
    const startMins = timeToMinutes(startTime);
    const endMins = timeToMinutes(endTime);
    const duration = endMins - startMins;
    const finalDuration = (duration > 0 && duration <= 480) ? duration : 30;
    const newEndMins = endMins + finalDuration;
    const hours = Math.floor(newEndMins / 60);
    const minutes = newEndMins % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  // Fonction pour récupérer tous les rendez-vous d'une série récurrente
  const fetchAllRecurringAppointments = useCallback(async (appointment) => {
    if (!appointment.recurrenceSeriesId) return [];

    try {
      const appointmentsRef = collection(db, 'appointments');
      const q = query(
        appointmentsRef,
        where('clientId', '==', client.id),
        where('recurrenceSeriesId', '==', appointment.recurrenceSeriesId)
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date?.toDate() || new Date(),
      }));
    } catch (error) {
      // Erreur silencieuse lors de la récupération des rendez-vous récurrents
      return [];
    }
  }, [db, client.id]);

  // Fonction pour mettre à jour un rendez-vous avec les nouvelles données
  const updateAppointmentWithData = async (appointmentId, newEmployeeId, newStatus, employeeName) => {
    const updateData = {
      employeeId: newEmployeeId,
      status: newStatus,
      employeeName: employeeName
    };

    // Nettoyer les données undefined
    Object.keys(updateData).forEach(key => {
      if (updateData[key] === undefined) {
        updateData[key] = null;
      }
    });

    try {
      await updateDoc(doc(db, 'appointments', appointmentId), updateData);
    } catch (error) {
      // Erreur silencieuse lors de la mise à jour du rendez-vous
      throw error;
    }
  };

  // Fonction pour gérer le déplacement d'une série de rendez-vous
  const handleRecurrenceMove = useCallback(async (scope, appointment, newEmployeeId, newStatus, employeeName, endDate = null) => {
    try {
      if (scope === 'this') {
        // Cette occurrence uniquement - briser la série
        await updateAppointmentWithData(appointment.id, newEmployeeId, newStatus, employeeName);

        // Supprimer l'ID de série pour briser la récurrence
        await updateDoc(doc(db, 'appointments', appointment.id), {
          recurrenceSeriesId: null,
          isRecurring: false
        });

      } else if (scope === 'future') {
        // Toutes les occurrences futures
        const allRecurringAppointments = await fetchAllRecurringAppointments(appointment);

        // Filtrer les occurrences futures (incluant celle-ci)
        const currentDate = new Date(appointment.date);
        currentDate.setHours(0, 0, 0, 0); // Normaliser l'heure pour la comparaison

        const appointmentsToUpdate = allRecurringAppointments.filter(app => {
          const appDate = new Date(app.date);
          appDate.setHours(0, 0, 0, 0); // Normaliser l'heure pour la comparaison
          return appDate >= currentDate;
        });

        // Mettre à jour tous les rendez-vous sélectionnés
        for (const app of appointmentsToUpdate) {
          await updateAppointmentWithData(app.id, newEmployeeId, newStatus, employeeName);
        }

      } else if (scope === 'period' && endDate) {
        // Remplacement temporaire jusqu'à une date spécifique - DÉPLACEMENT au lieu de SUPPRESSION
        const allRecurringAppointments = await fetchAllRecurringAppointments(appointment);

        // Dates de la période de remplacement
        const currentDate = new Date(appointment.date);
        currentDate.setHours(0, 0, 0, 0);
        const endDateObj = new Date(endDate);
        endDateObj.setHours(23, 59, 59, 999);

        // Filtrer les rendez-vous à DÉPLACER (dans la période)
        const appointmentsToMove = allRecurringAppointments.filter(app => {
          const appDate = new Date(app.date);
          appDate.setHours(0, 0, 0, 0);
          return appDate >= currentDate && appDate <= endDateObj;
        });

        // Créer une nouvelle série de remplacement
        const newSeriesId = doc(collection(db, 'appointments')).id;

        // DÉPLACER les rendez-vous vers la nouvelle série avec le nouvel intervenant
        const batch = writeBatch(db);
        for (const app of appointmentsToMove) {
          // Mettre à jour le rendez-vous existant avec le nouvel intervenant et la nouvelle série
          batch.update(doc(db, 'appointments', app.id), {
            employeeId: newEmployeeId,
            employeeName: employeeName,
            status: newStatus,
            recurrenceSeriesId: newSeriesId,
            // Conserver tous les autres paramètres (date, horaires, etc.)
          });
        }
        await batch.commit();
      }

      // Mettre à jour l'état local
      setAppointments(prevAppointments =>
        prevAppointments.map(app =>
          app.id === appointment.id
            ? {
              ...app,
              employeeId: newEmployeeId,
              status: newStatus,
              employeeName: employeeName
            }
            : app
        )
      );

      // Trigger a refresh
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      // Erreur silencieuse lors du déplacement de la série
    }
  }, [db, appointments, setRefreshTrigger, fetchAllRecurringAppointments]);

  // Fonction appelée quand l'utilisateur confirme le déplacement récurrent
  const handleRecurrenceMoveConfirm = useCallback(async (data) => {
    if (!pendingMoveData) {
      return;
    }

    const { appointment, newEmployeeId, newStatus, employeeName } = pendingMoveData;
    const { scope, endDate } = data;

    try {
      await handleRecurrenceMove(scope, appointment, newEmployeeId, newStatus, employeeName, endDate);
    } catch (error) {
      // Erreur silencieuse lors du déplacement récurrent
    }

    setShowRecurrenceMoveModal(false);
    setPendingMoveData(null);
  }, [pendingMoveData, handleRecurrenceMove]);

  // Fonction appelée quand l'utilisateur confirme la modification récurrente
  const handleRecurrenceUpdateConfirm = useCallback(async (scope) => {
    if (!pendingUpdateData) {
      return;
    }

    try {
      await handleUpdateAppointment(pendingUpdateData, scope);
    } catch (error) {
      // Erreur silencieuse lors de la modification récurrente
    }

    setShowRecurrenceUpdateModal(false);
    setPendingUpdateData(null);
  }, [pendingUpdateData, handleUpdateAppointment]);

  const handleDragEnd = useCallback(async (event) => {
    setActiveId(null);

    if (!canEditAppointments) {
      return;
    }

    const { active, over } = event;

    if (!over) {
      return;
    }

    // Empêcher le drop sur soi-même
    if (active.id === over.id) {
      return;
    }

    // Ensure the draggable appointment exists
    const appointment = appointments.find(app => app.id === active.id);
    if (!appointment) {
      return;
    }

    // Check if the droppableId corresponds to "unassigned" or an employee ID
    const newEmployeeId = over.id === 'unassigned' ? null : over.id;
    const newStatus = over.id === 'unassigned' ? 'libre' : 'assigne';

    // Vérifier si l'employé est en congé
    if (newEmployeeId && isEmployeeOnLeave(newEmployeeId)) {
      return;
    }

    // Validation des conflits horaires
    if (newEmployeeId) {
      const hasConflict = appointments.some(existingApp =>
        existingApp.employeeId === newEmployeeId &&
        existingApp.id !== appointment.id &&
        timeToMinutes(existingApp.startTime) < timeToMinutes(appointment.endTime) &&
        timeToMinutes(existingApp.endTime) > timeToMinutes(appointment.startTime)
      );

      if (hasConflict) {
        return;
      }
    }

    // Trouver le nom de l'employé
    let employeeName = '';
    if (newEmployeeId) {
      const employee = employees.find(emp => emp.userId === newEmployeeId);
      if (employee) {
        employeeName = employee.name || `${employee.firstName} ${employee.lastName}`;
      }
    }

    const isCtrlPressed = copyModeRef.current || window.isCopyModeContext; // Capturer l'état Ctrl

    // Si le mode copie est actif (Ctrl enfoncé)
    if (isCtrlPressed) {
      try {
        const newAppointment = {
          clientId: appointment.clientId,
          date: appointment.date,
          startTime: appointment.startTime, // Garder même startTime
          endTime: appointment.endTime,     // Garder même endTime
          status: newStatus,
          isRecurring: false, // On ne copie jamais la récurrence
        };

        if (appointment.details) newAppointment.details = appointment.details;
        if (appointment.notes) newAppointment.notes = appointment.notes;
        if (newEmployeeId) {
          newAppointment.employeeId = newEmployeeId;
          newAppointment.employeeName = employeeName;
        }

        await addDoc(collection(safeDb, 'appointments'), newAppointment);
        setRefreshTrigger(prev => prev + 1);
      } catch (error) {
        // Erreur silencieuse
      }

      setIsCopyMode(false);
      copyModeRef.current = false;
      window.isCopyModeContext = false;
      return;
    }

    // Si le rendez-vous est récurrent, afficher le modal de choix
    if (appointment.isRecurring && appointment.recurrenceSeriesId) {
      // Validation de sécurité pour éviter l'ouverture automatique
      if (!appointment.id || !appointment.isRecurring || !appointment.recurrenceSeriesId) {
        setIsCopyMode(false);
        copyModeRef.current = false;
        window.isCopyModeContext = false;
        return;
      }

      setPendingMoveData({
        appointment,
        newEmployeeId,
        newStatus,
        employeeName
      });
      setShowRecurrenceMoveModal(true);

      setIsCopyMode(false);
      copyModeRef.current = false;
      window.isCopyModeContext = false;
      return;
    }

    // Pour les rendez-vous non récurrents, procéder normalement
    try {
      await updateAppointmentWithData(appointment.id, newEmployeeId, newStatus, employeeName);

      // Optimistic update for immediate feedback
      setAppointments(prevAppointments =>
        prevAppointments.map(app =>
          app.id === appointment.id
            ? {
              ...app,
              employeeId: newEmployeeId,
              status: newStatus,
              employeeName: employeeName
            }
            : app
        )
      );

      // Trigger a refresh to ensure sync with backend
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      // Erreur silencieuse
    }

    setIsCopyMode(false);
    copyModeRef.current = false;
    window.isCopyModeContext = false;
  }, [appointments, canEditAppointments, employees, isEmployeeOnLeave, safeDb]);


  const AddAppointmentModal = ({ date, employees, onSave, onClose, initialEmployeeId }) => {
    const { theme } = useTheme();
    const isDarkMode = document.documentElement.classList.contains('dark');

    const [appointmentData, setAppointmentData] = useState(() => {
      const initialDate = new Date(date);
      initialDate.setUTCHours(2, 0, 0, 0);

      // Si on a un rendez-vous sélectionné (modification), pré-remplir les données
      if (selectedAppointment) {
        const appointmentDate = selectedAppointment.date instanceof Date
          ? selectedAppointment.date
          : new Date(selectedAppointment.date);

        return {
          ...selectedAppointment,
          date: appointmentDate,
          startTime: selectedAppointment.startTime || '',
          endTime: selectedAppointment.endTime || '',
          employeeId: selectedAppointment.employeeId || '',
          status: selectedAppointment.employeeId ? 'assigne' : 'libre',
          isRecurring: selectedAppointment.isRecurring || false,
          recurrenceBase: selectedAppointment.recurrenceBase || 'weekly',
          recurrenceFrequency: selectedAppointment.recurrenceFrequency || 1,
          recurrenceDay: selectedAppointment.recurrenceDay || [appointmentDate.getDay()],
          recurrenceMonthDay: selectedAppointment.recurrenceMonthDay || appointmentDate.getDate(),
          recurrenceMonthType: selectedAppointment.recurrenceMonthType || 'dayOfMonth',
          recurrenceMonthWeek: selectedAppointment.recurrenceMonthWeek || Math.floor((appointmentDate.getDate() - 1) / 7) + 1,
          recurrenceMonthWeekday: selectedAppointment.recurrenceMonthWeekday || appointmentDate.getDay(),
          recurrenceEndType: selectedAppointment.recurrenceEndType || 'never',
          recurrenceEndAfter: selectedAppointment.recurrenceEndAfter || 1,
          recurrenceEndDate: selectedAppointment.recurrenceEndDate || null,
        };
      }

      // Sinon, créer un nouveau rendez-vous avec les valeurs par défaut
      return {
        date: initialDate,
        startTime: '',
        endTime: '',
        employeeId: initialEmployeeId || '',
        status: initialEmployeeId ? 'assigne' : 'libre',
        isRecurring: false,
        recurrenceBase: 'weekly',
        recurrenceFrequency: 1,
        recurrenceDay: [initialDate.getDay()],
        recurrenceMonthDay: initialDate.getDate(),
        recurrenceMonthType: 'dayOfMonth',
        recurrenceMonthWeek: Math.floor((initialDate.getDate() - 1) / 7) + 1,
        recurrenceMonthWeekday: initialDate.getDay(),
        recurrenceEndType: 'never',
        recurrenceEndAfter: 1,
        recurrenceEndDate: null,
      };
    });

    const handleChange = (e) => {
      const { name, value, type, checked } = e.target;
      setAppointmentData(prev => {
        const newData = {
          ...prev,
          [name]: type === 'checkbox' ? checked :
            (name === 'recurrenceFrequency') ?
              Math.max(1, parseInt(value, 10) || 1) : value
        };

        if (name === 'employeeId') {
          newData.status = value ? 'assigne' : 'libre';
        }

        return newData;
      });
    };

    const handleDaySelection = (day) => {
      setAppointmentData(prev => ({
        ...prev,
        recurrenceDay: prev.recurrenceDay.includes(day)
          ? prev.recurrenceDay.filter(d => d !== day)
          : [...prev.recurrenceDay, day]
      }));
    };

    const handleSubmit = (e) => {
      e.preventDefault();
      const dateWithTime = new Date(appointmentData.date);
      dateWithTime.setUTCHours(2, 0, 0, 0);
      onSave({
        ...appointmentData,
        date: dateWithTime,
        status: appointmentData.employeeId ? 'assigne' : 'libre'
      });
    };

    const getDayOfWeekName = (day) => {
      const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
      return days[day];
    };

    const getWeekOfMonthName = (week) => {
      const weeks = ['premier', 'deuxième', 'troisième', 'quatrième', 'dernier'];
      return weeks[Math.min(week - 1, 4)];
    };

    return (
      <EnhancedModal onClose={onClose} size="xl">
        <ModalHeader>{selectedAppointment ? 'Modifier' : 'Ajouter'} un rendez-vous</ModalHeader>
        <ModalBody>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Section Informations de base */}
            <div className="space-y-4">
              <h3 className={`text-lg font-semibold border-b pb-2 ${isDarkMode ? 'text-gray-100 border-gray-700' : 'text-gray-900 border-gray-200'}`}>
                Informations de base
              </h3>

              {/* Date */}
              <div className="space-y-2">
                <label className={`flex items-center text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <Calendar className="mr-2 text-blue-500" size={16} />
                  Date du rendez-vous
                </label>
                <input
                  type="date"
                  name="date"
                  value={appointmentData.date.toISOString().split('T')[0]}
                  onChange={handleChange}
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 transition-colors ${isDarkMode
                    ? 'bg-gray-700 border-gray-600 text-gray-100 focus:border-blue-500'
                    : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                    }`}
                />
              </div>

              {/* Horaires */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className={`flex items-center text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <Clock className="mr-2 text-blue-500" size={16} />
                    Heure de début
                  </label>
                  <input
                    type="time"
                    name="startTime"
                    value={appointmentData.startTime}
                    onChange={handleChange}
                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 transition-colors ${isDarkMode
                      ? 'bg-gray-700 border-gray-600 text-gray-100 focus:border-blue-500'
                      : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                      }`}
                  />
                </div>

                <div className="space-y-2">
                  <label className={`flex items-center text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <Clock className="mr-2 text-blue-500" size={16} />
                    Heure de fin
                  </label>
                  <input
                    type="time"
                    name="endTime"
                    value={appointmentData.endTime}
                    onChange={handleChange}
                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 transition-colors ${isDarkMode
                      ? 'bg-gray-700 border-gray-600 text-gray-100 focus:border-blue-500'
                      : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                      }`}
                  />
                </div>
              </div>

              {/* Intervenant */}
              <div className="space-y-2">
                <label className={`flex items-center text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <User className="mr-2 text-blue-500" size={16} />
                  Intervenant assigné
                </label>
                <select
                  name="employeeId"
                  value={appointmentData.employeeId}
                  onChange={handleChange}
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 transition-colors ${isDarkMode
                    ? 'bg-gray-700 border-gray-600 text-gray-100 focus:border-blue-500'
                    : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                    }`}
                >
                  <option value="">Sélectionner un intervenant...</option>
                  {employees.map(emp => (
                    <option key={emp.userId} value={emp.userId}>
                      {emp.name || `${emp.firstName} ${emp.lastName}`}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Section Récurrence */}
            <div className="space-y-4">
              <div className={`flex items-center space-x-3 p-4 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
                }`}>
                <div className={`p-2 rounded-full ${appointmentData.isRecurring
                  ? isDarkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-600'
                  : isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-600'
                  }`}>
                  <RotateCcw size={20} />
                </div>
                <div className="flex-1">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="isRecurring"
                      checked={appointmentData.isRecurring}
                      onChange={handleChange}
                      className="sr-only"
                    />
                    <div className={`w-10 h-6 rounded-full transition-colors duration-200 ease-in-out ${appointmentData.isRecurring ? 'bg-green-500' : isDarkMode ? 'bg-gray-600' : 'bg-gray-300'
                      }`}>
                      <div className={`bg-white w-4 h-4 rounded-full transition-transform duration-200 ease-in-out transform ${appointmentData.isRecurring ? 'translate-x-6' : 'translate-x-1'
                        } mt-1`} />
                    </div>
                    <span className={`ml-3 text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                      Rendez-vous récurrent
                    </span>
                  </label>
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Planifier ce rendez-vous de manière répétée selon une fréquence définie
                  </p>
                </div>
              </div>
            </div>

            {appointmentData.isRecurring && (
              <div className="space-y-4 ml-7">
                <div>
                  <select
                    name="recurrenceBase"
                    value={appointmentData.recurrenceBase}
                    onChange={handleChange}
                    className={`w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'
                      }`}
                  >
                    <option value="daily">Journalière</option>
                    <option value="weekly">Hebdomadaire</option>
                    <option value="monthly">Mensuelle</option>
                  </select>
                </div>

                {appointmentData.recurrenceBase === 'daily' && (
                  <div className="flex items-center space-x-2">
                    <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>Tous les</span>
                    <input
                      type="number"
                      name="recurrenceFrequency"
                      value={appointmentData.recurrenceFrequency || 1}
                      onChange={handleChange}
                      min="1"
                      className={`w-16 p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'
                        }`}
                    />
                    <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>jour(s)</span>
                  </div>
                )}

                {appointmentData.recurrenceBase === 'weekly' && (
                  <>
                    <div className="flex items-center space-x-2">
                      <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>Tous les</span>
                      <input
                        type="number"
                        name="recurrenceFrequency"
                        value={appointmentData.recurrenceFrequency}
                        onChange={handleChange}
                        min="1"
                        className={`w-16 p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'
                          }`}
                      />
                      <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>semaine(s)</span>
                    </div>

                    <div className="space-y-2">
                      <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Jours de la semaine</p>
                      <div className="flex flex-wrap gap-2">
                        {[
                          { day: 0, label: 'Dim' },
                          { day: 1, label: 'Lun' },
                          { day: 2, label: 'Mar' },
                          { day: 3, label: 'Mer' },
                          { day: 4, label: 'Jeu' },
                          { day: 5, label: 'Ven' },
                          { day: 6, label: 'Sam' }
                        ].map(({ day, label }) => (
                          <button
                            key={day}
                            type="button"
                            onClick={() => handleDaySelection(day)}
                            className={`px-3 py-2 rounded text-sm font-medium transition-colors ${appointmentData.recurrenceDay.includes(day)
                              ? isDarkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'
                              : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                              }`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {appointmentData.recurrenceBase === 'monthly' && (
                  <>
                    <div>
                      <select
                        name="recurrenceMonthType"
                        value={appointmentData.recurrenceMonthType}
                        onChange={handleChange}
                        className={`w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'
                          }`}
                      >
                        <option value="dayOfMonth">Le même jour du mois</option>
                        <option value="dayOfWeek">Le même jour de la semaine</option>
                      </select>
                    </div>
                    {appointmentData.recurrenceMonthType === 'dayOfMonth' ? (
                      <div className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                        Répéter le {appointmentData.recurrenceMonthDay} de chaque mois
                      </div>
                    ) : (
                      <div className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                        Répéter le {getWeekOfMonthName(appointmentData.recurrenceMonthWeek)} {getDayOfWeekName(appointmentData.recurrenceMonthWeekday)} de chaque mois
                      </div>
                    )}
                    <div className="flex items-center space-x-2">
                      <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>Tous les</span>
                      <input
                        type="number"
                        name="recurrenceFrequency"
                        value={appointmentData.recurrenceFrequency}
                        onChange={handleChange}
                        min="1"
                        className={`w-16 p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'
                          }`}
                      />
                      <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>mois</span>
                    </div>
                  </>
                )}
                <div>
                  <select
                    name="recurrenceEndType"
                    value={appointmentData.recurrenceEndType}
                    onChange={handleChange}
                    className={`w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'
                      }`}
                  >
                    <option value="never">Jamais</option>
                    <option value="after">Après un nombre d'occurrences</option>
                    <option value="on">À une date spécifique</option>
                  </select>
                </div>
                {appointmentData.recurrenceEndType === 'after' && (
                  <div className="flex items-center space-x-2">
                    <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>Après</span>
                    <input
                      type="number"
                      name="recurrenceEndAfter"
                      value={appointmentData.recurrenceEndAfter}
                      onChange={handleChange}
                      min="1"
                      className={`w-16 p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'
                        }`}
                    />
                    <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>occurrences</span>
                  </div>
                )}
                {appointmentData.recurrenceEndType === 'on' && (
                  <div>
                    <input
                      type="date"
                      name="recurrenceEndDate"
                      value={appointmentData.recurrenceEndDate || ''}
                      onChange={handleChange}
                      className={`w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'
                        }`}
                    />
                  </div>
                )}
              </div>
            )}
          </form>
        </ModalBody>
        <ModalFooter>
          <ModalButton variant="secondary" onClick={onClose}>
            Annuler
          </ModalButton>
          <ModalButton variant="primary" onClick={handleSubmit}>
            {selectedAppointment ? 'Mettre à jour' : 'Ajouter'}
          </ModalButton>
        </ModalFooter>
      </EnhancedModal>
    );
  };


  const openingMinutes = timeToMinutes(settings.planningOpeningTime);
  const closingMinutes = timeToMinutes(settings.planningClosingTime);
  const totalMinutes = closingMinutes - openingMinutes;

  const calculatePosition = (time) => {
    const minutes = timeToMinutes(time) - openingMinutes;
    return (minutes / totalMinutes) * 100;
  };

  const calculateWidth = (start, end) => {
    const startMinutes = timeToMinutes(start) - openingMinutes;
    const endMinutes = timeToMinutes(end) - openingMinutes;
    return ((endMinutes - startMinutes) / totalMinutes) * 100;
  };

  const generateTimeSlots = () => {
    const slots = [];
    let currentTime = new Date(`2000-01-01T${settings.planningOpeningTime}`);
    const endTime = new Date(`2000-01-01T${settings.planningClosingTime}`);

    while (currentTime < endTime) {
      slots.push(currentTime.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }));
      currentTime.setMinutes(currentTime.getMinutes() + 30);  // Incrément de 30 minutes pour correspondre à la timeline
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  const sortAppointments = (apps) => {
    return apps.sort((a, b) => a.startTime.localeCompare(b.startTime));
  };

  const groupAppointmentsByEmployee = useCallback(() => {
    const grouped = {};
    employees.forEach(emp => {
      grouped[emp.userId] = sortAppointments(
        appointments.filter(app => app.employeeId === emp.userId)
      );
    });
    return grouped;
  }, [appointments, employees]);


  const getStatusClass = (status) => {
    switch (status) {
      case 'in_progress':
        return 'cdv-in-progress';
      case 'completed':
        return 'cdv-completed';
      case 'assigne':
        return 'cdv-assigned';
      case 'libre':
        return 'cdv-unassigned';
      default:
        return '';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'in_progress':
        return 'En cours';
      case 'completed':
        return 'Terminé';
      case 'assigne':
        return '';
      case 'libre':
        return 'À combler';
      default:
        return status;
    }
  };

  const renderStatusIcon = (status) => {
    switch (status) {
      case 'in_progress':
        return <Loader size={14} className="cdv-icon animate-spin" />;
      case 'completed':
        return <Check size={14} className="cdv-icon text-green-500" />;
      default:
        return null;
    }
  };

  const unassignedAppointments = appointments.filter(app => !app.employeeId || app.status === 'libre' || app.status === 'à combler');

  // Fonction pour obtenir les rendez-vous d'un employé spécifique
  const getEmployeeAppointments = (employeeId) => {
    return appointments.filter(app => app.employeeId === employeeId);
  };

  // Fonction pour générer les créneaux horaires de la journée
  const generateDayTimeline = () => {
    const slots = [];
    let currentTime = new Date(`2000-01-01T${settings.planningOpeningTime}`);
    const endTime = new Date(`2000-01-01T${settings.planningClosingTime}`);

    while (currentTime <= endTime) {
      slots.push(currentTime.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }));
      currentTime.setMinutes(currentTime.getMinutes() + 30); // Créneaux de 30 minutes
    }

    return slots;
  };

  const dayTimeline = generateDayTimeline();

  // Fonction pour calculer la position et la largeur d'un rendez-vous dans la timeline horizontale
  const getAppointmentPosition = (appointment) => {
    const startMinutes = timeToMinutes(appointment.startTime);
    const endMinutes = timeToMinutes(appointment.endTime);

    // Calculer le nombre de créneaux (de 30 minutes)
    const totalSlots = dayTimeline.length;

    // Calculer la position et la largeur en termes de créneaux
    const minutesFromStart = startMinutes - openingMinutes;
    const durationMinutes = endMinutes - startMinutes;

    // Convertir en index de créneau (chaque créneau = 30 minutes)
    const startSlot = minutesFromStart / 30;
    const durationInSlots = durationMinutes / 30;

    // Convertir en pourcentage basé sur le nombre de créneaux
    const left = (startSlot / totalSlots) * 100;
    const width = (durationInSlots / totalSlots) * 100;

    // S'assurer que les valeurs sont dans les limites valides
    const finalLeft = Math.max(0, Math.min(left, 100));
    const finalWidth = Math.max(0, Math.min(width, 100 - finalLeft));

    return {
      left: finalLeft,
      width: finalWidth
    };
  };





  // Fonction pour obtenir l'initiale d'un nom
  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Obtenir tous les IDs pour le contexte de drag
  const allAppointmentIds = appointments.map(app => app.id);

  // Récupérer le rendez-vous actif pour le DragOverlay
  const activeAppointment = activeId ? appointments.find(app => app.id === activeId) : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={allAppointmentIds} strategy={verticalListSortingStrategy}>
        <div className="cdv-container overflow-auto h-full">
          {/* En-tête avec la timeline */}
          <div className="cdv-timeline-header">
            <div className="cdv-employee-header">
              <span className="cdv-header-title">Intervenants</span>
            </div>
            <div className="cdv-time-header">
              {dayTimeline.map((time, index) => (
                <div key={index} className="cdv-time-slot">
                  {time}
                </div>
              ))}
            </div>
          </div>

          {/* Section des plages à combler */}
          <DroppableRow
            id="unassigned"
            className={`cdv-employee-row cdv-unassigned-row ${canEditAppointments ? 'cursor-pointer' : ''}`}
            onClick={() => handleTimelineClick(null)}
          >
            <div className="cdv-employee-info">
              <div className="cdv-employee-avatar cdv-unassigned-avatar">
                <span className="cdv-avatar-initials">?</span>
              </div>
              <div className="cdv-employee-details">
                <div className="cdv-employee-name cdv-unassigned-name">
                  Plages à combler
                </div>
              </div>
            </div>
            <div className="cdv-timeline-container">
              <div className="cdv-timeline-background">
                {dayTimeline.map((_, index) => (
                  <div key={index} className="cdv-timeline-slot"></div>
                ))}
              </div>
              <div className="cdv-appointments-timeline">
                {unassignedAppointments.map((appointment) => {
                  const position = getAppointmentPosition(appointment);
                  return (
                    <SortableAppointment
                      key={appointment.id}
                      appointment={appointment}
                      position={position}
                      getStatusClass={(status) => `cdv-unassigned ${getStatusClass(status)}`}
                      formatTime={formatTime}
                      handleAppointmentClick={handleAppointmentClick}
                      handleAppointmentDoubleClick={handleAppointmentDoubleClick}
                      canEditAppointments={canEditAppointments}
                    />
                  );
                })}
              </div>
            </div>
          </DroppableRow>

          {/* Lignes pour chaque employé */}
          {employees.map((employee) => {
            const employeeAppointments = appointments.filter(app => app.employeeId === employee.userId);
            const isOnLeave = isEmployeeOnLeave(employee.userId);
            return (
              <DroppableRow
                key={employee.userId}
                id={employee.userId}
                className={`cdv-employee-row ${isOnLeave ? 'cdv-on-leave' : ''} ${canEditAppointments && !isOnLeave ? 'cursor-pointer' : ''}`}
                onClick={() => !isOnLeave && handleTimelineClick(employee.userId)}
              >
                <div className="cdv-employee-info">
                  <div className="cdv-employee-avatar" style={{ backgroundColor: employee.color }}>
                    {employee.photoURL ? (
                      <img
                        src={employee.photoURL}
                        alt={employee.name || `${employee.firstName} ${employee.lastName}`}
                        className="cdv-avatar-image"
                      />
                    ) : (
                      <span className="cdv-avatar-initials">
                        {getInitials(employee.name || `${employee.firstName} ${employee.lastName}`)}
                      </span>
                    )}
                  </div>
                  <div className="cdv-employee-details">
                    <div className="cdv-employee-name">
                      {employee.name || `${employee.firstName} ${employee.lastName}`}
                    </div>
                    {isEmployeeOnLeave(employee.userId) && (
                      <div className="cdv-leave-indicator">
                        <Clock className="cdv-leave-icon" size={14} />
                        <span>En congé</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="cdv-timeline-container">
                  <div className="cdv-timeline-background">
                    {dayTimeline.map((_, index) => (
                      <div key={index} className="cdv-timeline-slot"></div>
                    ))}
                  </div>
                  {isEmployeeOnLeave(employee.userId) ? (
                    <div className="cdv-leave-block">
                      <span className="cdv-leave-text">En congé aujourd'hui</span>
                    </div>
                  ) : (
                    <div className="cdv-appointments-timeline">
                      {employeeAppointments.map((appointment) => {
                        const position = getAppointmentPosition(appointment);
                        return (
                          <SortableAppointment
                            key={appointment.id}
                            appointment={appointment}
                            position={position}
                            getStatusClass={getStatusClass}
                            formatTime={formatTime}
                            handleAppointmentClick={handleAppointmentClick}
                            handleAppointmentDoubleClick={handleAppointmentDoubleClick}
                            canEditAppointments={canEditAppointments}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
              </DroppableRow>
            );
          })}

          {isModalOpen && (
            <AddAppointmentModal
              date={currentDate}
              employees={employees}
              onSave={selectedAppointment ? handleUpdateAppointment : handleAddAppointment}
              onClose={() => {
                setIsModalOpen(false);
                setSelectedAppointment(null);
                setOpenInEditMode(false);
                setSelectedEmployeeId(null);
              }}
              initialEmployeeId={selectedAppointment?.employeeId || selectedEmployeeId}
            />
          )}
          {canEditAppointments && (
            <button
              onClick={() => {
                setSelectedAppointment(null);
                setIsModalOpen(true);
              }}
              className="cdv-add-button"
            >
              <Plus size={24} />
            </button>
          )}
        </div>
      </SortableContext>

      {/* Modal pour les déplacements de rendez-vous récurrents */}
      <RecurrenceMoveModal
        isOpen={showRecurrenceMoveModal}
        onClose={() => {
          setShowRecurrenceMoveModal(false);
          setPendingMoveData(null);
        }}
        onConfirm={handleRecurrenceMoveConfirm}
        appointment={pendingMoveData?.appointment}
      />

      {/* Modal pour les modifications de rendez-vous récurrents */}
      <RecurrenceEditModal
        isOpen={showRecurrenceUpdateModal}
        onClose={() => {
          setShowRecurrenceUpdateModal(false);
          setPendingUpdateData(null);
        }}
        onConfirm={handleRecurrenceUpdateConfirm}
        appointment={pendingUpdateData}
      />

      {/* Modal de consultation des détails du rendez-vous */}
      {isViewModalOpen && (
        <AppointmentViewModal
          appointment={selectedAppointment}
          employees={employees}
          onClose={() => {
            setIsViewModalOpen(false);
            setSelectedAppointment(null);
          }}
          onEdit={() => {
            setIsViewModalOpen(false);
            setIsModalOpen(true);
            setOpenInEditMode(true);
          }}
          onDelete={handleDeleteAppointment}
          hasPermission={hasPermission}
        />
      )}


      {/* DragOverlay pour un meilleur feedback visuel */}
      <DragOverlay>
        {activeAppointment ? (
          <div
            className={`cdv-timeline-appointment cdv-drag-overlay ${activeAppointment ? getStatusClass(activeAppointment.status) : ''}`}
            style={{
              width: '200px', // Largeur fixe pour le drag
              zIndex: 9999,
              boxShadow: '0 8px 25px rgba(0, 0, 0, 0.3)',
              opacity: 0.9,
              cursor: isCopyMode ? 'copy' : 'grabbing',
              transform: 'rotate(-2deg)', // Légère rotation pour l'effet de drag
              backgroundColor: isCopyMode ? '#10B981' : undefined,
              borderColor: isCopyMode ? '#059669' : undefined,
            }}
          >
            <div className="cdv-appointment-content" style={{ color: isCopyMode ? 'white' : undefined }}>
              <div className="cdv-appointment-time">
                {`${formatTime(activeAppointment.startTime)} - ${formatTime(activeAppointment.endTime)}`}
              </div>
              <div className="cdv-appointment-details">
                {activeAppointment.details || "Pas de détails"}
              </div>
              {activeAppointment.notes && activeAppointment.notes.content && (
                <div className="cdv-notes-indicator">
                  📝
                </div>
              )}
            </div>

            {isCopyMode && (
              <div className="cdv-copy-badge" style={{
                position: 'absolute',
                top: '-8px',
                right: '-8px',
                backgroundColor: '#059669',
                color: 'white',
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: 'bold',
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                📋 Copie
              </div>
            )}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
});

// Modal de détails et modification des rendez-vous
const AppointmentModal = ({ appointment, employees, onSave, onDelete, onClose, client, currentDate, hasPermission, openInEditMode }) => {
  const { theme } = useTheme();
  const isDarkMode = document.documentElement.classList.contains('dark');

  const [appointmentData, setAppointmentData] = useState(() => {
    if (appointment) {
      return {
        ...appointment,
        date: appointment.date instanceof Date ? appointment.date : new Date(appointment.date)
      };
    }
    return {
      startTime: '',
      endTime: '',
      employeeId: '',
      status: 'libre',
      date: currentDate,
      details: '',
      isRecurring: false,
      recurrenceBase: 'weekly',
      recurrenceFrequency: 1,
      recurrenceDay: [currentDate.getDay()],
      recurrenceMonthDay: currentDate.getDate(),
      recurrenceMonthType: 'dayOfMonth',
      recurrenceMonthWeek: Math.floor((currentDate.getDate() - 1) / 7) + 1,
      recurrenceMonthWeekday: currentDate.getDay(),
      recurrenceEndType: 'never',
      recurrenceEndAfter: 1,
      recurrenceEndDate: null,
    };
  });

  const [isEditing, setIsEditing] = useState(openInEditMode || false);
  const canEditAppointments = hasPermission ? hasPermission({ resource: 'appointments', action: 'write' }) : false;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAppointmentData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
      status: name === 'employeeId' ? (value ? 'assigne' : 'libre') : prev.status
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Si le rendez-vous est récurrent, afficher le modal de choix de portée
    if (appointmentData.isRecurring && appointmentData.recurrenceSeriesId) {
      setPendingUpdateData(appointmentData);
      setShowRecurrenceUpdateModal(true);
    } else {
      // Pour les rendez-vous non récurrents, sauvegarder directement
      onSave(appointmentData);
    }
  };

  const getDayOfWeekName = (day) => {
    const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    return days[day];
  };

  const getWeekOfMonthName = (week) => {
    const weeks = ['premier', 'deuxième', 'troisième', 'quatrième', 'dernier'];
    return weeks[Math.min(week - 1, 4)];
  };

  const handleDaySelection = (day) => {
    setAppointmentData(prev => ({
      ...prev,
      recurrenceDay: prev.recurrenceDay.includes(day)
        ? prev.recurrenceDay.filter(d => d !== day)
        : [...prev.recurrenceDay, day]
    }));
  };

  // Fonction pour obtenir le nom de l'employé
  const getEmployeeName = (employeeId) => {
    const employee = employees.find(emp => emp.userId === employeeId);
    return employee ? (employee.name || `${employee.firstName} ${employee.lastName}`) : 'Non assigné';
  };

  // Fonction pour obtenir le statut en français
  const getStatusText = (status) => {
    switch (status) {
      case 'assigne': return 'Assigné';
      case 'libre': return 'À combler';
      case 'in_progress': return 'En cours';
      case 'completed': return 'Terminé';
      default: return status;
    }
  };

  return (
    <EnhancedModal onClose={onClose} size="lg">
      <ModalHeader>
        {isEditing ? 'Modifier le rendez-vous' : 'Détails du rendez-vous'}
      </ModalHeader>
      <ModalBody>
        {!isEditing ? (
          // Mode consultation - Affichage des détails
          <div className="space-y-4">
            {/* Informations principales */}
            <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
              <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                Informations principales
              </h3>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Date</p>
                  <p className={isDarkMode ? 'text-gray-100' : 'text-gray-900'}>
                    {appointmentData.date.toLocaleDateString('fr-FR', {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </p>
                </div>

                <div>
                  <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Horaires</p>
                  <p className={isDarkMode ? 'text-gray-100' : 'text-gray-900'}>
                    {appointmentData.startTime} - {appointmentData.endTime}
                  </p>
                </div>

                <div>
                  <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Intervenant</p>
                  <p className={isDarkMode ? 'text-gray-100' : 'text-gray-900'}>
                    {getEmployeeName(appointmentData.employeeId)}
                  </p>
                </div>

                <div>
                  <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Statut</p>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${appointmentData.status === 'assigne'
                    ? isDarkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800'
                    : isDarkMode ? 'bg-yellow-900 text-yellow-300' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                    {getStatusText(appointmentData.status)}
                  </span>
                </div>
              </div>

            </div>

            {/* Informations de récurrence */}
            {appointmentData.isRecurring && (
              <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                  Récurrence
                </h3>

                <div className="space-y-2">
                  <p className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                    <span className="font-medium">Type:</span> {appointmentData.recurrenceBase === 'daily' ? 'Journalière' :
                      appointmentData.recurrenceBase === 'weekly' ? 'Hebdomadaire' : 'Mensuelle'}
                  </p>

                  {appointmentData.recurrenceBase === 'weekly' && (
                    <p className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                      <span className="font-medium">Jours:</span> {appointmentData.recurrenceDay.map(day => getDayOfWeekName(day)).join(', ')}
                    </p>
                  )}

                  {appointmentData.recurrenceBase === 'monthly' && appointmentData.recurrenceMonthType === 'dayOfWeek' && (
                    <p className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                      <span className="font-medium">Répétition:</span> Le {getWeekOfMonthName(appointmentData.recurrenceMonthWeek)} {getDayOfWeekName(appointmentData.recurrenceMonthWeekday)} de chaque mois
                    </p>
                  )}

                  {appointmentData.recurrenceEndType !== 'never' && (
                    <p className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                      <span className="font-medium">Fin:</span> {appointmentData.recurrenceEndType === 'after'
                        ? `Après ${appointmentData.recurrenceEndAfter} occurrences`
                        : `Le ${appointmentData.recurrenceEndDate ? new Date(appointmentData.recurrenceEndDate).toLocaleDateString('fr-FR') : 'Date non définie'}`}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Notes */}
            {appointmentData.notes && appointmentData.notes.content && (
              <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-blue-900/20 border-blue-700' : 'bg-blue-50 border-blue-200'}`}>
                <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-blue-300' : 'text-blue-900'}`}>
                  📝 Notes
                </h3>
                <p className={isDarkMode ? 'text-blue-200' : 'text-blue-800'}>
                  {appointmentData.notes.content}
                </p>
              </div>
            )}
          </div>
        ) : (
          // Mode édition - Formulaire de modification
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Section Informations de base */}
            <div className="space-y-4">
              <h3 className={`text-lg font-semibold border-b pb-2 ${isDarkMode ? 'text-gray-100 border-gray-700' : 'text-gray-900 border-gray-200'}`}>
                Informations de base
              </h3>

              {/* Date */}
              <div className="space-y-2">
                <label className={`flex items-center text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <Calendar className="mr-2 text-blue-500" size={16} />
                  Date du rendez-vous
                </label>
                <input
                  type="date"
                  name="date"
                  value={appointmentData.date.toISOString().split('T')[0]}
                  onChange={handleChange}
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 transition-colors ${isDarkMode
                    ? 'bg-gray-700 border-gray-600 text-gray-100 focus:border-blue-500'
                    : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                    }`}
                />
              </div>

              {/* Horaires */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className={`flex items-center text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <Clock className="mr-2 text-blue-500" size={16} />
                    Heure de début
                  </label>
                  <input
                    type="time"
                    name="startTime"
                    value={appointmentData.startTime}
                    onChange={handleChange}
                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 transition-colors ${isDarkMode
                      ? 'bg-gray-700 border-gray-600 text-gray-100 focus:border-blue-500'
                      : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                      }`}
                  />
                </div>

                <div className="space-y-2">
                  <label className={`flex items-center text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <Clock className="mr-2 text-blue-500" size={16} />
                    Heure de fin
                  </label>
                  <input
                    type="time"
                    name="endTime"
                    value={appointmentData.endTime}
                    onChange={handleChange}
                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 transition-colors ${isDarkMode
                      ? 'bg-gray-700 border-gray-600 text-gray-100 focus:border-blue-500'
                      : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                      }`}
                  />
                </div>
              </div>

              {/* Intervenant */}
              <div className="space-y-2">
                <label className={`flex items-center text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <User className="mr-2 text-blue-500" size={16} />
                  Intervenant assigné
                </label>
                <select
                  name="employeeId"
                  value={appointmentData.employeeId}
                  onChange={handleChange}
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 transition-colors ${isDarkMode
                    ? 'bg-gray-700 border-gray-600 text-gray-100 focus:border-blue-500'
                    : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                    }`}
                >
                  <option value="">Sélectionner un intervenant...</option>
                  {employees.map(emp => (
                    <option key={emp.userId} value={emp.userId}>
                      {emp.name || `${emp.firstName} ${emp.lastName}`}
                    </option>
                  ))}
                </select>
              </div>

            </div>

            {/* Section Récurrence */}
            {appointmentData.isRecurring && (
              <div className="space-y-4">
                <h3 className={`text-lg font-semibold border-b pb-2 ${isDarkMode ? 'text-gray-100 border-gray-700' : 'text-gray-900 border-gray-200'}`}>
                  Paramètres de récurrence
                </h3>

                <div>
                  <select
                    name="recurrenceBase"
                    value={appointmentData.recurrenceBase}
                    onChange={handleChange}
                    className={`w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'
                      }`}
                  >
                    <option value="daily">Journalière</option>
                    <option value="weekly">Hebdomadaire</option>
                    <option value="monthly">Mensuelle</option>
                  </select>
                </div>

                {appointmentData.recurrenceBase === 'weekly' && (
                  <div className="space-y-2">
                    <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Jours de la semaine</p>
                    <div className="flex flex-wrap gap-2">
                      {[0, 1, 2, 3, 4, 5, 6].map(day => (
                        <button
                          key={day}
                          type="button"
                          onClick={() => handleDaySelection(day)}
                          className={`px-3 py-2 rounded text-sm font-medium transition-colors ${appointmentData.recurrenceDay.includes(day)
                            ? isDarkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'
                            : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                            }`}
                        >
                          {getDayOfWeekName(day).substring(0, 3)}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </form>
        )}
      </ModalBody>
      <ModalFooter>
        {!isEditing ? (
          // Mode consultation - Boutons de consultation
          <div className="flex justify-between w-full">
            <ModalButton variant="secondary" onClick={onClose}>
              Fermer
            </ModalButton>
            {canEditAppointments && appointmentData.status !== 'completed' && (
              <div className="flex space-x-2">
                <ModalButton
                  variant="danger"
                  onClick={() => onDelete(appointmentData.id)}
                >
                  Supprimer
                </ModalButton>
                <ModalButton
                  variant="primary"
                  onClick={() => setIsEditing(true)}
                >
                  Modifier
                </ModalButton>
              </div>
            )}
            {canEditAppointments && appointmentData.status === 'completed' && (
              <div className="flex space-x-2">
                <ModalButton
                  variant="danger"
                  onClick={() => onDelete(appointmentData.id)}
                >
                  Supprimer
                </ModalButton>
              </div>
            )}
          </div>
        ) : (
          // Mode édition - Boutons d'édition
          <div className="flex justify-between w-full">
            <ModalButton
              variant="secondary"
              onClick={() => setIsEditing(false)}
            >
              Annuler
            </ModalButton>
            <div className="flex space-x-2">
              <ModalButton
                variant="danger"
                onClick={() => onDelete(appointmentData.id)}
              >
                Supprimer
              </ModalButton>
              <ModalButton
                variant="primary"
                onClick={handleSubmit}
              >
                Mettre à jour
              </ModalButton>
            </div>
          </div>
        )}
      </ModalFooter>
    </EnhancedModal>
  );
};

// Ajouter le displayName pour le débogage
ClientDayView.displayName = 'ClientDayView';

export default ClientDayView;
